import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, RefreshCw, Trash2, Edit2, Database, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Instruction, DatabaseConnection } from '../../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const Instructions: React.FC = () => {
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [connections, setConnections] = useState<DatabaseConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState<Instruction | null>(null);
  const [instructionText, setInstructionText] = useState('');

  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      fetchInstructions();
    }
  }, [selectedConnection]);

  const fetchConnections = async () => {
    try {
      const data = await api.getDatabaseConnections();
      setConnections(data);
      if (data.length > 0 && !selectedConnection) {
        setSelectedConnection(data[0].id || '');
      }
    } catch (error) {
      toast.error('Failed to fetch database connections');
    }
  };

  const fetchInstructions = async () => {
    if (!selectedConnection) return;
    
    setLoading(true);
    try {
      const data = await api.getInstructions({
        db_connection_id: selectedConnection,
        page: 1,
        limit: 100,
      });
      setInstructions(data);
    } catch (error) {
      toast.error('Failed to fetch instructions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedConnection || !instructionText.trim()) {
      toast.error('Please provide an instruction');
      return;
    }

    try {
      if (editingInstruction) {
        await api.updateInstruction(editingInstruction.id, {
          instruction: instructionText,
        });
        toast.success('Instruction updated successfully');
      } else {
        await api.createInstruction({
          db_connection_id: selectedConnection,
          instruction: instructionText,
        });
        toast.success('Instruction created successfully');
      }
      
      setShowForm(false);
      setEditingInstruction(null);
      setInstructionText('');
      fetchInstructions();
    } catch (error) {
      toast.error('Failed to save instruction');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this instruction?')) {
      return;
    }

    try {
      await api.deleteInstruction(id);
      toast.success('Instruction deleted successfully');
      fetchInstructions();
    } catch (error) {
      toast.error('Failed to delete instruction');
    }
  };

  const handleEdit = (instruction: Instruction) => {
    setEditingInstruction(instruction);
    setInstructionText(instruction.instruction);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingInstruction(null);
    setInstructionText('');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="border-b border-[#e6e6ea] px-5 py-3 shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-[18px] font-semibold text-[#22212d]">Instructions</h1>
          <div className="flex items-center gap-2">
            <Select value={selectedConnection} onValueChange={setSelectedConnection}>
              <SelectTrigger className="h-7 w-[200px] text-[13px] border-[#d8d8de]">
                <SelectValue placeholder="Select a database..." />
              </SelectTrigger>
              <SelectContent>
                {connections.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id || ''} className="text-[13px]">
                    {conn.alias}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchInstructions}
              disabled={loading || !selectedConnection}
              className="h-7 px-2.5 text-[13px] font-medium border-[#d8d8de]"
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={() => setShowForm(true)}
              disabled={!selectedConnection}
              variant="gradient"
              size="sm"
              className="h-7 px-2.5 text-[13px]"
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add Instruction
            </Button>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-6">
        <Card className="bg-white border-[#e6e6ea]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[16px] font-semibold text-[#22212d]">
            All Instructions
          </CardTitle>
          <CardDescription className="text-[13px] text-[rgba(34,33,45,0.6)]">
            Define domain-specific rules and business logic for better SQL generation
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Alert className="mb-4 border-primary/20 bg-primary/5">
            <AlertDescription className="text-[13px] text-[#22212d]">
              <strong>Instructions</strong> are domain-specific rules that help the SQL generator understand your database better.
              For example: "Customer ID is stored in the 'cust_id' column", "Revenue is calculated as price * quantity",
              or "Always filter deleted records where is_deleted = false".
            </AlertDescription>
          </Alert>

          {!selectedConnection ? (
            <div className="text-center py-12">
              <Database className="h-12 w-12 text-[rgba(34,33,45,0.5)] mx-auto mb-4" />
              <p className="text-[14px] text-[rgba(34,33,45,0.6)]">Please select a database connection</p>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : instructions.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-[rgba(34,33,45,0.5)] mx-auto mb-4" />
              <p className="text-[14px] text-[rgba(34,33,45,0.6)] mb-4">No instructions found</p>
              <Button onClick={() => setShowForm(true)} variant="gradient">
                Add Your First Instruction
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {instructions.map((instruction) => (
                <Card key={instruction.id} className="hover:shadow-md transition-shadow bg-white border-[#e6e6ea]">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-[14px] text-[#22212d]">{instruction.instruction}</p>
                        <p className="text-[12px] text-[rgba(34,33,45,0.5)] mt-2">
                          Added: {new Date(instruction.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-1 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(instruction)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(instruction.id)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Form Modal */}
      <Dialog open={showForm} onOpenChange={handleCancel}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingInstruction ? 'Edit Instruction' : 'Add Instruction'}
            </DialogTitle>
            <DialogDescription>
              Provide clear, specific instructions about your database schema, business rules, or query patterns
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="instruction">
                Instruction <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="instruction"
                value={instructionText}
                onChange={(e) => setInstructionText(e.target.value)}
                rows={4}
                placeholder="e.g., Customer ID is stored in the 'cust_id' column in the customers table"
                required
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingInstruction ? 'Update' : 'Create'} Instruction
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Instructions;